// api.ts — docdaemon's REST client and the whole type vocabulary its API
// speaks. Every interface below mirrors a Go DTO in products/docdaemon/server
// field for field (json tags are snake_case, so the names match verbatim); the
// functions under them are the only place a route string is written, so a
// server-side rename breaks one file rather than five pages.
import { createApiClient, unwrapJson } from 'common';

export const apiClient = createApiClient({ tokenKey: 'docdaemon_token' });

// The authz scopes this SPA reads. The server gates the same routes on the
// same strings (-admin-scope, -docs-scope, -cost-scope), so hiding a control
// here hides something the API would refuse anyway — which is the only honest
// reason to hide it. `docdaemon.view` is not listed: it gates the whole /api
// subrouter, so a session without it never gets far enough to render a page.
export const ADMIN_SCOPE = 'docdaemon.admin';
export const DOCS_SCOPE = 'docdaemon.docs.read';
export const COST_SCOPE = 'docdaemon.cost.read';

// hasScope answers off /api/me's scope list, which the shared useAuth hook
// carries verbatim. Scopes rather than roles: a role is a bundle whose contents
// are authz's business and can be re-cut without this app knowing, but the
// scope a route demands is the contract between the two services.
export function hasScope(auth: { scopes: string[] }, scope: string): boolean {
  return auth.scopes.includes(scope);
}

// isAdmin gates the four write controls — add repo, remove repo, force
// reindex, pause/resume — and nothing else.
export function isAdmin(auth: { scopes: string[] }): boolean {
  return hasScope(auth, ADMIN_SCOPE);
}

// ----- DTOs -----

// Repo is one watched repository. `status` is derived server-side from the
// run table rather than stored, so it reflects work in flight; last_indexed_*
// describe the last run that actually committed.
export interface Repo {
  id: string;
  owner: string;
  name: string;
  full_name: string;
  default_branch: string;
  html_url: string;
  clone_url: string;
  enabled: boolean;
  last_indexed_sha: string;
  last_indexed_at: string | null;
  doc_count: number;
  status: RepoStatus;
  last_error: string;
}

export type RepoStatus = 'idle' | 'queued' | 'running' | 'error' | 'never-indexed';

// Run is one pass over a repository: what triggered it, the commit range it
// covered, how many jobs it decomposed into, and — once it has pushed — the
// documentation commit it authored as docdaemon itself.
export interface Run {
  id: string;
  repo_full_name: string;
  trigger: RunTrigger;
  head_sha: string;
  base_sha: string;
  status: RunStatus;
  started_at: string;
  finished_at: string | null;
  total_jobs: number;
  done_jobs: number;
  failed_jobs: number;
  file_jobs: number;
  dir_jobs: number;
  max_level: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
  commit_sha: string;
  error: string;
}

export type RunTrigger = 'webhook' | 'poll' | 'manual' | 'bootstrap';

export type RunStatus = 'pending' | 'running' | 'committing' | 'complete' | 'failed' | 'skipped';

// Job is one LLM call's worth of work. `level` is the ordering the daemon
// guarantees: every level-0 file summary finishes before any directory summary
// starts, and directories then run deepest-first up to the repo root.
export interface Job {
  id: string;
  run_id: string;
  kind: JobKind;
  path: string;
  level: number;
  status: JobStatus;
  model: string;
  attempt: number;
  prompt_tokens: number;
  completion_tokens: number;
  duration_ms: number;
  error: string;
}

export type JobKind = 'file' | 'directory' | 'reduce';

export type JobStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

// DocNode is one entry in the recorded documentation tree. `path` is the
// *source* path — the file or directory that was summarised — and `children`
// is present on directories only. has_doc is false for anything the daemon
// walked but has not written a summary for yet, which is why it is a separate
// field from tldr rather than "tldr is empty".
export interface DocNode {
  name: string;
  path: string;
  kind: 'dir' | 'file';
  has_doc: boolean;
  tldr: string;
  generated_at: string | null;
  children?: DocNode[];
}

// DocBlob is one generated document: the markdown, plus the provenance the
// daemon writes into its header comment (which model wrote it, and the hash of
// the input it was written from — the cache key that decides re-generation).
export interface DocBlob {
  path: string;
  doc_path: string;
  content: string;
  generated_at: string | null;
  model: string;
  content_hash: string;
}

// Status is the daemon's own vital signs. The overflow trio is the interesting
// part: past `overflow_threshold` pending items the model selector switches
// from the local model to OpenRouter, and back once the queue drains below
// `overflow_clear` — so `active_model` is a fact about load, not configuration.
export interface Status {
  queue_depth: number;
  in_flight: number;
  workers: number;
  active_model: string;
  base_model: string;
  overflow_model: string;
  overflow_active: boolean;
  overflow_threshold: number;
  overflow_clear: number;
  repos_total: number;
  repos_enabled: number;
  runs_active: number;
  last_run_at: string | null;
  auto_commit: boolean;
  paused: boolean;
  jobs_completed_total: number;
  jobs_failed_total: number;
  tokens_per_min: number;
  // Throughput as a rate, over the same one-minute window. Per second because
  // that is the unit a model's generation speed is quoted in, so the figure
  // can be compared against what the model is supposed to do.
  tokens_per_sec: number;
  // The same window split by the model that produced each sample, busiest
  // first. A window spanning a failover holds work from both models, and this
  // is the only place that says so.
  tokens_per_sec_by_model: ModelThroughput[];
}

export interface ModelThroughput {
  model: string;
  tokens_per_sec: number;
}

export interface ModelCost {
  model: string;
  requests: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
}

export interface RepoCost {
  repo_full_name: string;
  runs: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
}

export interface CostPoint {
  ts: string;
  cost_usd: number;
  tokens: number;
}

// CostSummary is the ledger over a window of hours, sliced three ways: by
// model (what the overflow switch costs), by repo (which repository is
// expensive), and over time (whether it is getting worse).
export interface CostSummary {
  window_hours: number;
  requests: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
  by_model: ModelCost[];
  by_repo: RepoCost[];
  series: CostPoint[];
}

// GitCommit is a commit as gitea reports it, with the one bit this app adds:
// whether docdaemon authored it. That is what makes the panel readable — a
// repo's history is mostly other people's work, and the question being asked
// of it is "did my documentation land".
export interface GitCommit {
  sha: string;
  message: string;
  author_name: string;
  author_email: string;
  committed_at: string;
  html_url: string;
  by_docdaemon: boolean;
}

// ----- Requests -----

// Owner and name reach us from the router, so they are user input as far as
// this file is concerned: encode them rather than trusting a repository name
// to be path-safe.
function repoPath(owner: string, name: string): string {
  return `${encodeURIComponent(owner)}/${encodeURIComponent(name)}`;
}

export async function fetchStatus(): Promise<Status> {
  return unwrapJson<Status>(await apiClient.fetch('/api/status'));
}

export async function fetchRepos(): Promise<Repo[]> {
  const data = await unwrapJson<{ repos: Repo[] }>(await apiClient.fetch('/api/repos'));
  return data.repos ?? [];
}

export async function fetchRepo(owner: string, name: string): Promise<{ repo: Repo; runs: Run[] }> {
  const data = await unwrapJson<{ repo: Repo; runs: Run[] }>(
    await apiClient.fetch(`/api/repos/${repoPath(owner, name)}`),
  );
  return { repo: data.repo, runs: data.runs ?? [] };
}

export async function addRepo(fullName: string): Promise<Repo> {
  const data = await unwrapJson<{ repo: Repo }>(await apiClient.fetch('/api/repos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ full_name: fullName }),
  }));
  return data.repo;
}

export async function removeRepo(owner: string, name: string): Promise<void> {
  await unwrapJson(await apiClient.fetch(`/api/repos/${repoPath(owner, name)}`, { method: 'DELETE' }));
}

// reindexRepo queues a run by hand. `full` re-derives every document instead of
// only the paths the last push touched, which is what you want after a prompt
// change and never want by accident — it is a whole-repository bill.
export async function reindexRepo(owner: string, name: string, full: boolean): Promise<Run> {
  const data = await unwrapJson<{ run: Run }>(
    await apiClient.fetch(`/api/repos/${repoPath(owner, name)}/index`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ full }),
    }),
  );
  return data.run;
}

// pauseRepo takes `enabled`, not `paused`: the field it sets is the repo's own
// enabled flag, so the button label inverts and the wire format does not.
export async function pauseRepo(owner: string, name: string, enabled: boolean): Promise<Repo> {
  const data = await unwrapJson<{ repo: Repo }>(
    await apiClient.fetch(`/api/repos/${repoPath(owner, name)}/pause`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }),
  );
  return data.repo;
}

export async function fetchRuns(opts: { limit?: number; repo?: string } = {}): Promise<Run[]> {
  const query = new URLSearchParams();
  if (opts.limit !== undefined) query.set('limit', String(opts.limit));
  if (opts.repo) query.set('repo', opts.repo);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await unwrapJson<{ runs: Run[] }>(await apiClient.fetch(`/api/runs${suffix}`));
  return data.runs ?? [];
}

export async function fetchRun(id: string): Promise<{ run: Run; jobs: Job[] }> {
  const data = await unwrapJson<{ run: Run; jobs: Job[] }>(
    await apiClient.fetch(`/api/runs/${encodeURIComponent(id)}`),
  );
  return { run: data.run, jobs: data.jobs ?? [] };
}

export async function fetchDocTree(owner: string, name: string): Promise<DocNode> {
  const data = await unwrapJson<{ tree: DocNode }>(
    await apiClient.fetch(`/api/docs/${repoPath(owner, name)}/tree`),
  );
  return data.tree;
}

export async function fetchDocBlob(owner: string, name: string, path: string): Promise<DocBlob> {
  const query = new URLSearchParams({ path });
  return unwrapJson<DocBlob>(
    await apiClient.fetch(`/api/docs/${repoPath(owner, name)}/blob?${query.toString()}`),
  );
}

export async function fetchCost(hours: number): Promise<CostSummary> {
  return unwrapJson<CostSummary>(await apiClient.fetch(`/api/cost?hours=${hours}`));
}

export async function fetchCommits(owner: string, name: string, limit: number): Promise<GitCommit[]> {
  const data = await unwrapJson<{ commits: GitCommit[] }>(
    await apiClient.fetch(`/api/gitea/${repoPath(owner, name)}/commits?limit=${limit}`),
  );
  return data.commits ?? [];
}

// --- Source, commits and cleanups -------------------------------------------

// SourceFile is a file's actual contents, read live from the repository rather
// than from the page describing it.
export interface SourceFile {
  path: string;
  content: string;
  truncated: boolean;
  // A highlighter hint from the extension; '' when the extension says nothing.
  language: string;
}

export interface CommitFile {
  filename: string;
  status: string;
  additions: number;
  deletions: number;
}

// CommitDetail is one commit with its diff. `diff` is capped server-side, and
// `diff_truncated` says when what you have is a prefix — a merge runs to
// megabytes and showing the first half silently would be worse than saying so.
export interface CommitDetail {
  sha: string;
  html_url: string;
  message: string;
  author_name: string;
  author_email: string;
  committed_at: string;
  additions: number;
  deletions: number;
  files: CommitFile[];
  diff: string;
  diff_truncated: boolean;
  by_docdaemon: boolean;
}

// CommitExplanation is the prose docdaemon generated for a commit. Cached
// forever: a commit is immutable, so explaining it twice would buy the same
// answer at the same price.
export interface CommitExplanation {
  repo_full_name: string;
  sha: string;
  summary: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
  created_at: string;
}

export type CleanupSeverity = 'high' | 'medium' | 'low';

// CleanupFinding is one proposed change. reviewed_sha is the commit the
// directory was at when the review ran, so a stale report can say so instead
// of looking current.
export interface CleanupFinding {
  id: string;
  repo_full_name: string;
  dir_path: string;
  path: string;
  kind: string;
  severity: CleanupSeverity;
  title: string;
  rationale: string;
  suggestion: string;
  reviewed_sha: string;
  created_at: string;
  // The Gitea issue this finding was filed as, or 0 while it is still only an
  // opinion. Recorded rather than inferred, so the panel offers to file once
  // and links thereafter — and so a re-review that restates the same finding
  // keeps the link instead of offering to file a duplicate.
  issue_number: number;
  issue_url: string;
  issue_filed_at?: string;
  // Set once an administrator has set the finding aside. The list never
  // returns a dismissed finding; this arrives only on the dismiss response.
  dismissed_at?: string;
  dismissed_by?: string;
  // Why it was set aside, when the administrator said. Carried across a
  // re-review with the dismissal, and shown to the next review of the
  // directory so it does not make the same point again in other words.
  dismissed_reason?: string;
}

export async function fetchSource(owner: string, name: string, path: string): Promise<SourceFile> {
  const query = new URLSearchParams({ path });
  return unwrapJson<SourceFile>(
    await apiClient.fetch(`/api/docs/${repoPath(owner, name)}/source?${query.toString()}`),
  );
}

export async function fetchCommit(owner: string, name: string, sha: string): Promise<{
  commit: CommitDetail;
  explanation?: CommitExplanation;
}> {
  return unwrapJson<{ commit: CommitDetail; explanation?: CommitExplanation }>(
    await apiClient.fetch(`/api/gitea/${repoPath(owner, name)}/commits/${encodeURIComponent(sha)}`),
  );
}

// explainCommit is a POST because the first call spends money. It is
// idempotent after that, so pressing it twice costs once.
export async function explainCommit(owner: string, name: string, sha: string): Promise<CommitExplanation> {
  const data = await unwrapJson<{ explanation: CommitExplanation }>(
    await apiClient.fetch(`/api/gitea/${repoPath(owner, name)}/commits/${encodeURIComponent(sha)}/explain`, {
      method: 'POST',
    }),
  );
  return data.explanation;
}

export async function fetchCleanups(owner: string, name: string, path: string): Promise<CleanupFinding[]> {
  const query = new URLSearchParams();
  if (path) query.set('path', path);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await unwrapJson<{ findings: CleanupFinding[] }>(
    await apiClient.fetch(`/api/repos/${repoPath(owner, name)}/cleanups${suffix}`),
  );
  return data.findings ?? [];
}

// proposeCleanups re-reviews a directory, replacing whatever it said before.
// Admin-gated server-side: it spends money on every call and there is no
// immutable key to cache it under.
export async function proposeCleanups(owner: string, name: string, path: string): Promise<CleanupFinding[]> {
  const query = new URLSearchParams();
  if (path) query.set('path', path);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await unwrapJson<{ findings: CleanupFinding[] }>(
    await apiClient.fetch(`/api/repos/${repoPath(owner, name)}/cleanups${suffix}`, { method: 'POST' }),
  );
  return data.findings ?? [];
}

// fileCleanupIssue opens a Gitea issue for one finding and returns the finding
// with its issue link recorded.
//
// Admin-gated server-side, and idempotent: a finding remembers the issue it
// became, so a second press returns the first issue rather than opening a
// duplicate. Returns the whole finding rather than just the number so the
// caller can replace the row it has instead of patching a field into it.
export async function fileCleanupIssue(
  owner: string,
  name: string,
  id: string,
): Promise<CleanupFinding> {
  const data = await unwrapJson<{ finding: CleanupFinding }>(
    await apiClient.fetch(
      `/api/repos/${repoPath(owner, name)}/cleanups/${encodeURIComponent(id)}/issue`,
      { method: 'POST' },
    ),
  );
  return data.finding;
}

// dismissCleanup sets one finding aside, so it leaves the panel and stays gone
// when a re-review restates it.
//
// Admin-gated server-side, and refused (409) for a finding already filed as an
// issue — the issue is where that decision lives now.
//
// reason is optional; the server caps it at 1000 characters. Sent only when
// non-empty, so a dismissal without one is the same bodiless request it always
// was. Dismissing an already-dismissed finding with a reason records the reason
// if it had none, keeping the original time and author.
export async function dismissCleanup(
  owner: string,
  name: string,
  id: string,
  reason = '',
): Promise<CleanupFinding> {
  const trimmed = reason.trim();
  const init: RequestInit = trimmed
    ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: trimmed }),
      }
    : { method: 'POST' };
  const data = await unwrapJson<{ finding: CleanupFinding }>(
    await apiClient.fetch(
      `/api/repos/${repoPath(owner, name)}/cleanups/${encodeURIComponent(id)}/dismiss`,
      init,
    ),
  );
  return data.finding;
}
